<script setup lang="ts">
import { computed, ref } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Popover from '@uiKit/elements/Popover.vue'
import InitialsAvatar from '@uiKit/components/InitialsAvatar.vue'
import Text from '@uiKit/elements/Text.vue'
import { maskAccountNumber } from '@shared/utils'
import type { BankAccount } from '@modules/account'

// Destination picker for a withdrawal, anchored under the "Change" button
// (Figma 32590-77489) rather than a second centred dialog. Selecting commits
// immediately and closes — an extra Done step between "pick a bank" and the
// withdrawal's own Confirm would be friction twice.
interface Props {
  accounts: BankAccount[]
  selectedId: string
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'select', account: BankAccount): void
}>()

const popoverRef = ref<InstanceType<typeof Popover> | null>(null)

const isVerified = (account: BankAccount) => account.accountStatus === 'verified'

const statusLabel = (account: BankAccount): string => {
  if (account.accountStatus === 'rejected') return 'Rejected'
  return 'Pending verification'
}

const sortedAccounts = computed(() =>
  // Usable destinations first; an unverified account can't receive funds.
  [...props.accounts].sort(
    (a, b) =>
      Number(isVerified(b)) - Number(isVerified(a)) || Number(b.isPrimary) - Number(a.isPrimary),
  ),
)

const onSelect = (account: BankAccount) => {
  if (!isVerified(account)) return
  emit('select', account)
  popoverRef.value?.hide()
}

defineExpose({
  toggle: (event: Event, target?: HTMLElement | null) => popoverRef.value?.toggle(event, target),
  hide: () => popoverRef.value?.hide(),
})
</script>

<template>
  <Popover ref="popoverRef">
    <Box direction="col" gap="16px" class="sbp">
      <Text type="heading" weight="semibold" is="h2">Select Bank Account</Text>

      <Box direction="col" gap="10px" class="sbp__list">
        <Box
          v-for="account in sortedAccounts"
          :key="account.id"
          is="button"
          align="center"
          gap="14px"
          :class="[
            'sbp__item',
            {
              'sbp__item--selected': selectedId === account.id,
              'sbp__item--disabled': !isVerified(account),
            },
          ]"
          type="button"
          :disabled="!isVerified(account)"
          :aria-pressed="selectedId === account.id"
          @click="onSelect(account)"
        >
          <InitialsAvatar
            :name="account.bankName"
            :src="account.logoSrc"
            :letters="2"
            :size="38"
            fit="contain"
            radius="10px"
          />

          <Box direction="col" gap="1" grow class="sbp__info">
            <Text type="body" weight="medium" align="left" is="span" truncate>
              {{ account.bankName }}
            </Text>
            <Text type="caption" color="muted" align="left" is="span">
              {{ maskAccountNumber(account.accountNumber) }}
              <template v-if="account.isPrimary"> · Default</template>
              <template v-else-if="!isVerified(account)"> · {{ statusLabel(account) }}</template>
            </Text>
          </Box>

          <Box flex align="center" justify="center" class="sbp__radio">
            <Box v-if="selectedId === account.id" class="sbp__radio-dot" />
          </Box>
        </Box>

        <Text v-if="!accounts.length" type="body" color="muted" align="center" is="p">
          No bank accounts found.
        </Text>
      </Box>
    </Box>
  </Popover>
</template>

<style scoped>
.sbp {
  width: 304px;
  padding: 18px 16px;
  box-sizing: border-box;
}

.sbp__list {
  /* Room for the focus ring, which the scroll box would otherwise clip. */
  margin: -4px;
  padding: 4px;
  overflow-y: auto;
  max-height: 50vh;
}

.sbp__item {
  width: 100%;
  background: var(--p-surface-0);
  border: 1px solid var(--p-surface-300);
  border-radius: 12px;
  padding: 0.75rem 0.875rem;
  cursor: pointer;
  text-align: left;
  box-sizing: border-box;
  transition:
    border-color 0.15s,
    background 0.15s;
}

.sbp__item:hover:not(:disabled) {
  border-color: var(--p-primary-200);
  background: var(--p-primary-50);
}

.sbp__item:focus-visible {
  outline: 2px solid var(--p-primary-600);
  outline-offset: 2px;
}

.sbp__item--selected {
  border: 1.5px solid var(--p-primary-600);
  padding: calc(0.75rem - 0.5px) calc(0.875rem - 0.5px);
}

.sbp__item--disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.sbp__info {
  min-width: 0;
}

.sbp__radio {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1.5px solid var(--p-surface-300);
  flex-shrink: 0;
}

.sbp__item--selected .sbp__radio {
  border-color: var(--p-primary-600);
}

.sbp__radio-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--p-primary-600);
}
</style>
